import { getEcallsForErefId } from "../quotationViewer.helpers"
import type { TEcall, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"

// Pure case table of the split-screen SMART NAVIGATION machine (#1311). No React, no Apryse — the
// whole "which pane becomes what, and what does the other one show" decision lives here so it can
// be asserted directly.
//
// The design enumerates seven cases; they collapse to FOUR states and TWO code paths (decision D7).
// The rule, verbatim from the spec:
//
//   A selection makes the pane it was made in the PRIMARY pane. The other pane becomes SECONDARY
//   and auto-displays the COUNTERPART entity — an eref selection yields the connected ecall(s), an
//   ecall selection yields the connected eref(s) — with a jumping-navigation session over that set.
//
// So the only branch below is on the selected entity's KIND (eref → ecalls, ecall → erefs). The
// pane half of each state is pure arithmetic on where the click happened, which is why Cases 1/5/7
// resolve to one plan and Cases 4/6 to another rather than to seven separate branches.
//
// A furniture selection is a THIRD path, added on top of D7 (PR #1347 review): a furniture's
// counterpart is always its parent eref's ecalls — the same `getEcallsForErefId` lookup the eref
// path already uses, just indirected one hop through `furniture.eref_id`. There is no reverse
// direction (ecall/eref selected → furniture) — furniture is a source only, never a counterpart.

export type TSmartNavSelection =
	| { kind: "eref"; eref: TErefWithFurniture }
	| { kind: "ecall"; ecall: TEcall }
	| { kind: "furniture"; furniture: TFurniture }

// The ordered set the SECONDARY pane steps through. Its `kind` is always the complement of the
// selection's.
export type TSmartNavCounterparts = { kind: "ecall"; items: TEcall[] } | { kind: "eref"; items: TErefWithFurniture[] }

export type TSmartNavPlan = {
	// The pane the selection was made in — it keeps showing what was selected and carries the
	// active-pane border.
	primaryPane: TViewerPane
	// The pane that follows: it shows the counterpart at `index` and hosts the jumping-nav bar.
	secondaryPane: TViewerPane
	selection: TSmartNavSelection
	counterparts: TSmartNavCounterparts
	// Where in `counterparts.items` the session starts. 0 for a session that has never run before;
	// the remembered position when Primary flips back to a pane (decision D8 — "reuse of previous
	// eCall navigation").
	index: number
	// Identity of this (pane, entity) session, so its position can be remembered across flips.
	memoryKey: string
}

// Remembered session positions, keyed by `memoryKey`. A plain record so the caller can hold it in a
// ref and this module stays free of state.
export type TSmartNavIndexMemory = Record<string, number>

export const getSecondaryPane = (primaryPane: TViewerPane): TViewerPane =>
	primaryPane === "primary" ? "split" : "primary"

export const getSelectionEntityId = (selection: TSmartNavSelection): number => {
	if (selection.kind === "eref") return selection.eref.id
	if (selection.kind === "ecall") return selection.ecall.id
	return selection.furniture.id
}

// The erefs an ecall points at. De-duplicated because one callout can carry several elevation-number
// rows resolving to the same reference, and `eref_id` is null on a row that has not been connected.
export const getConnectedErefIds = (ecall: TEcall): number[] =>
	Array.from(new Set(ecall.elevation_numbers.map((row) => row.eref_id).filter((id): id is number => id != null)))

// A session is identified by the pane the selection was made in PLUS the entity, not by the pane
// alone: flipping Primary back to a pane only restores a position if the same entity is selected
// there again — a different entity has a different counterpart list, so its index would be
// meaningless.
export const getSmartNavMemoryKey = (primaryPane: TViewerPane, selection: TSmartNavSelection): string =>
	`${primaryPane}:${selection.kind}:${getSelectionEntityId(selection)}`

export type TSmartNavInput = {
	selection: TSmartNavSelection
	// The pane the selection was made in.
	pane: TViewerPane
	// Every ecall in the quotation — the eref → ecalls direction is a filter over this.
	allEcalls: TEcall[]
	// The ecall → erefs direction resolves ids through the caller's cache lookup.
	findErefById: (erefId: number) => TErefWithFurniture | undefined
	indexMemory?: TSmartNavIndexMemory
}

// Resolve one selection into the full Primary/Secondary plan. Always returns a plan (the pane roles
// flip on any selection, whether or not there is anything to show on the other side); an empty
// `counterparts.items` is the caller's signal that no session should be started.
export const resolveSmartNavigationPlan = ({
	selection,
	pane,
	allEcalls,
	findErefById,
	indexMemory,
}: TSmartNavInput): TSmartNavPlan => {
	const memoryKey = getSmartNavMemoryKey(pane, selection)
	const counterparts: TSmartNavCounterparts =
		selection.kind === "eref"
			? { kind: "ecall", items: getEcallsForErefId(allEcalls, selection.eref.id) }
			: selection.kind === "furniture"
				? { kind: "ecall", items: getEcallsForErefId(allEcalls, selection.furniture.eref_id) }
				: {
						kind: "eref",
						items: getConnectedErefIds(selection.ecall)
							.map(findErefById)
							.filter((eref): eref is TErefWithFurniture => eref != null),
					}
	const remembered = indexMemory?.[memoryKey] ?? 0
	// Clamped rather than dropped: the counterpart list is re-derived from live data on every
	// selection, so a remembered position can legitimately point past the end of a list that has
	// shrunk since (a callout was deleted, a connection was removed).
	const index = counterparts.items.length === 0 ? 0 : Math.max(0, Math.min(remembered, counterparts.items.length - 1))

	return {
		primaryPane: pane,
		secondaryPane: getSecondaryPane(pane),
		selection,
		counterparts,
		index,
		memoryKey,
	}
}
